import type { Language } from "@/shared/lib/language";
import type { DateFormat } from "@/shared/lib/date-format";
import { DEFAULT_NAV_CURRENCY, isNavCurrency, type NavCurrency } from "@/shared/lib/nav-currency";

export type WireLanguage = "en-US" | "pt-BR";
export type WireDateFormat = "year-month-day" | "day-month-year" | "month-day-year";
export type NumberFormat = "comma-period" | "period-comma" | "space-comma";

function inverted<K extends string, V extends string>(map: Record<K, V>): Record<V, K> {
  return Object.fromEntries(
    Object.entries(map).map(([key, value]) => [value, key]),
  ) as Record<V, K>;
}

function isKeyOf<K extends string>(map: Record<K, unknown>, value: unknown): value is K {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(map, value);
}

export const wireLanguageByLanguage: Record<Language, WireLanguage> = {
  en: "en-US",
  pt: "pt-BR",
};
export const languageByWireLanguage = inverted(wireLanguageByLanguage);

export const wireDateFormatByDateFormat: Record<DateFormat, WireDateFormat> = {
  "YYYY-MM-DD": "year-month-day",
  "DD/MM/YYYY": "day-month-year",
  "MM/DD/YYYY": "month-day-year",
};
export const dateFormatByWireDateFormat = inverted(wireDateFormatByDateFormat);

export const exampleByNumberFormat: Record<NumberFormat, string> = {
  "comma-period": "1,234.56",
  "period-comma": "1.234,56",
  "space-comma": "1 234,56",
};

export const LANGUAGES = Object.keys(wireLanguageByLanguage) as Language[];
export const DATE_FORMATS = Object.keys(wireDateFormatByDateFormat) as DateFormat[];
export const NUMBER_FORMATS = Object.keys(exampleByNumberFormat) as NumberFormat[];
export const DEFAULT_NUMBER_FORMAT: NumberFormat = "comma-period";

export const isLanguage = (value: unknown): value is Language =>
  isKeyOf(wireLanguageByLanguage, value);

export const isDateFormat = (value: unknown): value is DateFormat =>
  isKeyOf(wireDateFormatByDateFormat, value);

export interface UserSetting {
  id: string;
  user_id: string;
  language: WireLanguage | null;
  date_format: WireDateFormat | null;
  currency: NavCurrency | null;
  number_format: NumberFormat | null;
  created: string;
}

const knownOrNull = <K extends string>(map: Record<K, unknown>, value: unknown): K | null =>
  isKeyOf(map, value) ? value : null;

export function userSettingFrom(value: unknown): UserSetting | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  const textFields = [candidate.id, candidate.user_id, candidate.created];
  if (!textFields.every((field) => typeof field === "string")) return null;
  return {
    id: candidate.id as string,
    user_id: candidate.user_id as string,
    language: knownOrNull(languageByWireLanguage, candidate.language),
    date_format: knownOrNull(dateFormatByWireDateFormat, candidate.date_format),
    currency: isNavCurrency(candidate.currency) ? candidate.currency : null,
    number_format: knownOrNull(exampleByNumberFormat, candidate.number_format),
    created: candidate.created as string,
  };
}

export const isSavedSetting = (setting: UserSetting): boolean => setting.id !== "";

export type UserSettingPatch = Partial<
  Pick<UserSetting, "language" | "date_format" | "currency" | "number_format">
>;

export interface SettingValues {
  language: Language;
  dateFormat: DateFormat;
  currency: NavCurrency;
  numberFormat: NumberFormat;
}

export type SettingField = keyof SettingValues;

const wireFieldBySettingField: Record<SettingField, keyof UserSettingPatch> = {
  language: "language",
  dateFormat: "date_format",
  currency: "currency",
  numberFormat: "number_format",
};

export const SETTING_FIELDS = Object.keys(wireFieldBySettingField) as SettingField[];

export const SETTING_FIELD_MAP: Record<string, string> = Object.fromEntries(
  SETTING_FIELDS.map((field) => [`setting.${wireFieldBySettingField[field]}`, field]),
);

export function appliedSettingValues(
  applied: { language: Language; dateFormat: DateFormat },
  stored: UserSetting,
): SettingValues {
  return {
    language: applied.language,
    dateFormat: applied.dateFormat,
    currency: stored.currency ?? DEFAULT_NAV_CURRENCY,
    numberFormat: stored.number_format ?? DEFAULT_NUMBER_FORMAT,
  };
}

function wireSettingFrom(values: SettingValues): Required<UserSettingPatch> {
  return {
    language: wireLanguageByLanguage[values.language],
    date_format: wireDateFormatByDateFormat[values.dateFormat],
    currency: values.currency,
    number_format: values.numberFormat,
  };
}

export const changedSettingFields = (values: SettingValues, applied: SettingValues): SettingField[] =>
  SETTING_FIELDS.filter((field) => values[field] !== applied[field]);

export function settingPatchFrom(
  values: SettingValues,
  applied: SettingValues,
  stored: UserSetting,
): UserSettingPatch {
  const wireValues = wireSettingFrom(values);
  const changedFields = changedSettingFields(values, applied);
  const sentFields = SETTING_FIELDS.filter((field) => {
    const wireField = wireFieldBySettingField[field];
    const storedValue = stored[wireField];
    return changedFields.includes(field)
      || storedValue === null
      || (!isSavedSetting(stored) && wireValues[wireField] !== storedValue);
  });
  return Object.fromEntries(
    sentFields.map((field) => {
      const wireField = wireFieldBySettingField[field];
      return [wireField, wireValues[wireField]];
    }),
  ) as UserSettingPatch;
}
